// A Reporting section's title with its description folded away (2026-10-27,
// Patrick's asks: the explanations under each section took a lot of room, and the
// section titles should stand out). The title is a button with a chevron; the
// description is hidden until it is clicked and is always hidden on a fresh load.
//
// Two looks. 'section' (default) is a bold, larger title with a rule under it; the
// rule is as wide as the box the heading sits in, so
// wrap the heading and its table in a `width: fit-content` box and the rule ends
// where the table ends, which lets two sections sit side by side. 'quiet' is the
// plain title, for notes such as "About this tab". `sx` is the margin the title
// used to carry.
import { useId, useState } from 'react'
import { Box, ButtonBase, Collapse, SvgIcon, Typography } from '@mui/material'

function Chevron({ open }) {
  return (
    <SvgIcon
      fontSize="small"
      sx={{ transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none', color: 'text.secondary' }}
    >
      <path d="M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
    </SvgIcon>
  )
}

const TITLE_SX = {
  subtitle1: { fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.35 },
  subtitle2: { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35 },
}

const BAR_SX = {
  pb: 0.5,
  borderBottom: '1px solid',
  borderBottomColor: 'divider',
}

export default function SectionHeading({ title, variant = 'subtitle1', appearance = 'section', sx, descriptionMaxWidth, children }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const emphasized = appearance === 'section'
  const titleSx = emphasized ? TITLE_SX[variant] ?? {} : {}
  const barSx = emphasized ? BAR_SX : {}
  // A heading with no description is just a heading.
  if (children == null || children === false) {
    return (
      <Box sx={sx}>
        <Box sx={barSx}>
          <Typography variant={variant} sx={titleSx}>{title}</Typography>
        </Box>
      </Box>
    )
  }
  return (
    <Box sx={sx}>
      <Box sx={barSx}>
        <ButtonBase
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={id}
          title={open ? 'Hide the description' : 'Show what this section is'}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, textAlign: 'left', borderRadius: 1, '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' } }}
        >
          <Typography variant={variant} component="span" sx={titleSx}>{title}</Typography>
          <Chevron open={open} />
        </ButtonBase>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Typography id={id} variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 1, maxWidth: descriptionMaxWidth }}>
          {children}
        </Typography>
      </Collapse>
    </Box>
  )
}
